/**
 * Script Blocks Cloud Storage API
 * 
 * Stores scriptBlocks as a gzipped JSON file in Cloud Storage to avoid
 * Firestore's 1MB document limit for large scripts.
 * 
 * Storage path: organizations/{orgId}/projects/{projectId}/system/scriptBlocks.json.gz
 * Character hints: organizations/{orgId}/projects/{projectId}/system/characterHints.json
 */

import { getBlobStoreClient } from '@/backend';
import { isBlobObjectNotFoundError } from '@/backend/contracts';
import { ScriptBlock } from '@/features/show-structure/types';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { logger } from '@/utils/logger';
import { buildProjectDocPath, type ProjectScope } from './project-scope';
import { enqueueArtifactFinalization } from './artifact-write';

const SCRIPT_BLOCKS_FILE_NAME = 'scriptBlocks.json.gz';
const CLASSIFIED_STRUCTURE_FILE_NAME = 'classifiedStructure.json';
const EXTRACTED_PROPS_FILE_NAME = 'extractedProps.json';
const EXTRACTED_SOUND_CUES_FILE_NAME = 'extractedSoundCues.json';

const SCRIPT_BLOCKS_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system/${SCRIPT_BLOCKS_FILE_NAME}`;

const CHARACTER_HINTS_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system/characterHints.json`;

const SYSTEM_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system`;

export type CharacterHint = { isEnsemble: boolean; notes?: string };

function parseJson<T>(text: string): T {
    const parsed: unknown = JSON.parse(text);
    return parsed as T;
}

function storagePathFileName(storagePath: string): string | null {
    return storagePath.split('/').pop() || null;
}

async function listSystemFileNames(scope: ProjectScope): Promise<Set<string> | null> {
    try {
        const result = await getBlobStoreClient().list({ prefix: SYSTEM_PATH(scope) });
        return new Set(result.map(storagePathFileName).filter((fileName): fileName is string => Boolean(fileName)));
    } catch (error: unknown) {
        if (isBlobObjectNotFoundError(error)) {
            return new Set();
        }

        logger.warn('[ScriptStorage] Failed to list project system files; falling back to direct artifact loads:', error);
        return null;
    }
}

function hasListedFile(fileNames: Set<string> | null, fileName: string): boolean {
    return fileNames === null || fileNames.has(fileName);
}

/**
 * Compresses a string using gzip via CompressionStream API
 */
async function gzipCompress(data: string): Promise<Blob> {
    const encoder = new TextEncoder();
    const stream = new ReadableStream({
        start(controller) {
            controller.enqueue(encoder.encode(data));
            controller.close();
        }
    });
    
    const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
    const response = new Response(compressedStream);
    return await response.blob();
}

/**
 * Decompresses gzipped data using DecompressionStream API
 * Returns null if data is not actually gzipped
 */
async function gzipDecompress(data: ArrayBuffer): Promise<string | null> {
    // Check gzip magic numbers: 0x1F, 0x8B
    const view = new Uint8Array(data);
    if (view.length < 2 || view[0] !== 0x1F || view[1] !== 0x8B) {
        logger.sync('Data does not have gzip magic bytes, skipping decompression.');
        return null;
    }

    try {
        const stream = new ReadableStream({
            start(controller) {
                controller.enqueue(view);
                controller.close();
            }
        });
        
        const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
        const response = new Response(decompressedStream);
        return await response.text();
    } catch (error: unknown) {
        // Data might be corrupted or not valid gzip despite the headers/magic bytes
        logger.sync('Decompression failed despite matching magic bytes:', error);
        return null;
    }
}

/**
 * Generic Cloud Storage JSON loader.
 * Handles: download URL → fetch → parse → fallback on error.
 * Excludes scriptBlocks which has special gzip handling.
 */
async function loadFromStorage<T>(
    scope: ProjectScope,
    pathFn: (scope: ProjectScope) => string,
    label: string,
    fallback: T
): Promise<T> {
    const result = await tryLoadFromStorage(scope, pathFn, label, fallback);
    return result.value;
}

type StorageLoadResult<T> =
    | { loaded: true; value: T }
    | { loaded: false; reason: 'missing'; value: T }
    | { loaded: false; reason: 'failed'; value: T };

async function tryLoadFromStorage<T>(
    scope: ProjectScope,
    pathFn: (scope: ProjectScope) => string,
    label: string,
    fallback: T
): Promise<StorageLoadResult<T>> {
    try {
        const storagePath = pathFn(scope);
        const url = await getBlobStoreClient().getDownloadUrl({ storagePath });
        const response = await fetch(withCacheBuster(url), { cache: 'no-store' });

        if (!response.ok) {
            logger.warn(`[${label}] HTTP ${response.status}`);
            return {
                loaded: false,
                reason: response.status === 404 ? 'missing' : 'failed',
                value: fallback,
            };
        }

        const data: unknown = await response.json();
        logger.sync(`${label} loaded successfully`);
        return { loaded: true, value: data as T };
    } catch (error: unknown) {
        if (isBlobObjectNotFoundError(error)) {
            logger.sync(`${label} no file found for project:`, scope.projectId);
            return { loaded: false, reason: 'missing', value: fallback };
        }
        logger.warn(`[${label}] Failed:`, error);
        return { loaded: false, reason: 'failed', value: fallback };
    }
}

function withCacheBuster(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}onbookCacheBust=${Date.now()}`;
}

/**
 * Saves scriptBlocks to Cloud Storage as a gzipped JSON file
 */
export async function saveScriptBlocks(
    scope: ProjectScope,
    scriptBlocks: ScriptBlock[]
): Promise<{ storagePath: string; size: number }> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to save script blocks.');
    
    // Convert to JSON and compress
    const jsonString = JSON.stringify(scriptBlocks);
    const compressedBlob = await gzipCompress(jsonString);
    
    const storagePath = SCRIPT_BLOCKS_PATH(scope);
    
    // Upload with metadata
    const uploadMetadata = {
        contentType: 'application/gzip',
        customMetadata: {
            originalSize: jsonString.length.toString(),
            blockCount: scriptBlocks.length.toString(),
            uploadedBy: userId,
            uploadedAt: new Date().toISOString()
        }
    };
    const uploadFile = new File([compressedBlob], SCRIPT_BLOCKS_FILE_NAME, {
        type: uploadMetadata.contentType,
    });
    const uploadTask = getBlobStoreClient().upload({
        id: storagePath,
        storagePath,
        contentType: uploadMetadata.contentType,
        customMetadata: uploadMetadata.customMetadata,
    }, uploadFile);
    await uploadTask.finished;

    await enqueueArtifactFinalization({
        scope,
        actorId: userId,
        feature: 'script_show',
        capability: 'EDIT_RUNSHEET',
        conflictUnit: 'scriptShow.artifacts/scriptBlocks',
        artifactType: 'scriptBlocks',
        entityId: 'scriptBlocks',
        storagePath,
        contentType: uploadMetadata.contentType,
        metadata: {
            blockCount: scriptBlocks.length,
            originalSize: jsonString.length,
            size: compressedBlob.size,
        },
    });
    
    return {
        storagePath,
        size: compressedBlob.size
    };
}

/**
 * Loads scriptBlocks from Cloud Storage (special gzip handling)
 */
export async function loadScriptBlocks(scope: ProjectScope): Promise<ScriptBlock[]> {
    const storagePath = SCRIPT_BLOCKS_PATH(scope);

    try {
        const downloadUrl = await getBlobStoreClient().getDownloadUrl({ storagePath });
        const response = await fetch(withCacheBuster(downloadUrl), { cache: 'no-store' });

        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
        }

        // Check if CDN auto-decompressed the gzip content
        const contentType = response.headers.get('Content-Type') || '';
        const isStillGzipped = contentType.includes('gzip') || contentType.includes('octet-stream');

        let jsonString: string;

        if (isStillGzipped) {
            // Response is still gzipped — decompress manually
            const compressedData = await response.arrayBuffer();
            const decompressed = await gzipDecompress(compressedData);
            if (decompressed === null) {
                // Fallback: Content-Type was wrong, try raw decode
                jsonString = new TextDecoder().decode(compressedData);
            } else {
                jsonString = decompressed;
            }
        } else {
            // CDN already decompressed — just read text
            jsonString = await response.text();
        }

        const blocks = parseJson<ScriptBlock[]>(jsonString);
        logger.sync('Loaded', blocks.length, 'blocks');
        return blocks;
    } catch (error: unknown) {
        if (isBlobObjectNotFoundError(error)) {
            logger.sync('No script blocks found:', scope.projectId);
            return [];
        }
        logger.error('[ScriptStorage] Load failed:', { projectId: scope.projectId, error });
        throw error;
    }
}

/**
 * Deletes scriptBlocks from Cloud Storage
 */
export async function deleteScriptBlocks(scope: ProjectScope): Promise<void> {
    const storagePath = SCRIPT_BLOCKS_PATH(scope);
    
    try {
        await getBlobStoreClient().cleanup({ storagePath });
    } catch (error: unknown) {
        // Ignore if file doesn't exist
        if (isBlobObjectNotFoundError(error)) {
            return;
        }
        throw error;
    }
}

/**
 * Gets the download URL for the scriptBlocks file (for archival/export)
 */
export async function getScriptBlocksDownloadUrl(scope: ProjectScope): Promise<string | null> {
    const storagePath = SCRIPT_BLOCKS_PATH(scope);
    
    try {
        return await getBlobStoreClient().getDownloadUrl({ storagePath });
    } catch (error: unknown) {
        if (isBlobObjectNotFoundError(error)) {
            return null;
        }
        throw error;
    }
}

/**
 * Loads characterHints from Cloud Storage (generated by AI during script analysis)
 */
export async function loadCharacterHints(scope: ProjectScope): Promise<Record<string, CharacterHint>> {
    return loadFromStorage(scope, CHARACTER_HINTS_PATH, 'loadCharacterHints', {});
}

/**
 * Classified structure from Pro model Pass 2
 */
export interface ClassifiedStructure {
    acts: Array<{ id: string; name: string; isPreset: boolean; sceneIds: string[] }>;
    scenes: Array<{ id: string; name: string; notes?: string; type?: 'scene' | 'song'; songNumber?: string; parentSceneId?: string | null }>;
    characters: Array<{ name: string; isEnsemble: boolean; notes?: string; sceneIds: string[] }>;
}

const CLASSIFIED_STRUCTURE_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system/${CLASSIFIED_STRUCTURE_FILE_NAME}`;

/**
 * Loads Pro-classified structure from Cloud Storage (Pass 2 results)
 */
export async function loadClassifiedStructure(scope: ProjectScope): Promise<ClassifiedStructure | null> {
    return loadFromStorage(scope, CLASSIFIED_STRUCTURE_PATH, 'loadClassifiedStructure', null);
}

// Extracted props type from backend
export interface ExtractedProp {
    id: string;
    name: string;
    sceneId?: string;
    assignedCharacterId?: string | null;
    type?: string;
    notes?: string;
}

const EXTRACTED_PROPS_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system/${EXTRACTED_PROPS_FILE_NAME}`;

const JOB_EXTRACTED_PROPS_PATH = (scope: ProjectScope, jobId: string) =>
    `${buildProjectDocPath(scope)}/system/imports/${jobId}/${EXTRACTED_PROPS_FILE_NAME}`;

export interface LoadProductionElementArtifactsOptions {
    jobId?: string;
}

/**
 * Load extracted props from Cloud Storage (saved during script analysis)
 */
export async function loadExtractedProps(
    scope: ProjectScope,
    options: LoadProductionElementArtifactsOptions = {}
): Promise<ExtractedProp[]> {
    const { jobId } = options;
    if (jobId) {
        const jobScopedResult = await tryLoadFromStorage(
            scope,
            scoped => JOB_EXTRACTED_PROPS_PATH(scoped, jobId),
            'loadExtractedProps',
            []
        );
        if (jobScopedResult.loaded) {
            return jobScopedResult.value;
        }
        if (jobScopedResult.reason === 'failed') {
            throw new Error('loadExtractedProps failed to load job-scoped artifact');
        }
    }

    return loadFromStorage(scope, EXTRACTED_PROPS_PATH, 'loadExtractedProps', []);
}

// Extracted sound cue type from backend
export interface ExtractedSoundCue {
    id: string;
    description: string;
    sceneId?: string;
    cueLine?: string;
    notes?: string;
    cueNumber?: string;
}

const EXTRACTED_SOUND_CUES_PATH = (scope: ProjectScope) =>
    `${buildProjectDocPath(scope)}/system/${EXTRACTED_SOUND_CUES_FILE_NAME}`;

const JOB_EXTRACTED_SOUND_CUES_PATH = (scope: ProjectScope, jobId: string) =>
    `${buildProjectDocPath(scope)}/system/imports/${jobId}/${EXTRACTED_SOUND_CUES_FILE_NAME}`;

/**
 * Load extracted sound cues from Cloud Storage (saved during script analysis)
 */
export async function loadExtractedSoundCues(
    scope: ProjectScope,
    options: LoadProductionElementArtifactsOptions = {}
): Promise<ExtractedSoundCue[]> {
    const { jobId } = options;
    if (jobId) {
        const jobScopedResult = await tryLoadFromStorage(
            scope,
            scoped => JOB_EXTRACTED_SOUND_CUES_PATH(scoped, jobId),
            'loadExtractedSoundCues',
            []
        );
        if (jobScopedResult.loaded) {
            return jobScopedResult.value;
        }
        if (jobScopedResult.reason === 'failed') {
            throw new Error('loadExtractedSoundCues failed to load job-scoped artifact');
        }
    }

    return loadFromStorage(scope, EXTRACTED_SOUND_CUES_PATH, 'loadExtractedSoundCues', []);
}

export interface ScriptImportArtifacts {
    scriptBlocks: ScriptBlock[];
    classifiedStructure: ClassifiedStructure | null;
    extractedProps: ExtractedProp[];
    extractedSoundCues: ExtractedSoundCue[];
}

interface LoadScriptImportArtifactsOptions {
    includeScriptBlocks?: boolean;
    includeProductionElements?: boolean;
}

/**
 * Loads optional script-import artifacts after first listing the project system folder.
 * Missing artifacts are normal for new projects and metadata-only imports; listing avoids
 * visible browser 404s from probing every optional file with getDownloadURL().
 */
export async function loadScriptImportArtifacts(
    scope: ProjectScope,
    options: LoadScriptImportArtifactsOptions = {}
): Promise<ScriptImportArtifacts> {
    const includeScriptBlocks = options.includeScriptBlocks ?? true;
    const includeProductionElements = options.includeProductionElements ?? true;
    const fileNames = await listSystemFileNames(scope);

    const [scriptBlocks, classifiedStructure, extractedProps, extractedSoundCues] = await Promise.all([
        includeScriptBlocks && hasListedFile(fileNames, SCRIPT_BLOCKS_FILE_NAME)
            ? loadScriptBlocks(scope)
            : Promise.resolve([]),
        hasListedFile(fileNames, CLASSIFIED_STRUCTURE_FILE_NAME)
            ? loadClassifiedStructure(scope)
            : Promise.resolve(null),
        includeProductionElements && hasListedFile(fileNames, EXTRACTED_PROPS_FILE_NAME)
            ? loadExtractedProps(scope)
            : Promise.resolve([]),
        includeProductionElements && hasListedFile(fileNames, EXTRACTED_SOUND_CUES_FILE_NAME)
            ? loadExtractedSoundCues(scope)
            : Promise.resolve([]),
    ]);

    return {
        scriptBlocks,
        classifiedStructure,
        extractedProps,
        extractedSoundCues,
    };
}
